import React, { useState } from 'react';
import axios from 'axios';
import { Snackbar, Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useTimeclockStore } from '../../../storeTimeclock';
import { getOrganizationId } from '../../../store';
import ReopenConfirmDialog from './dialogs/ReopenConfirmDialog';
import PSwitchDialog from './dialogs/PSwitchDialog';

/**
 * Shared clock-action controller used by every surface (tablet, top-bar pill, WO panel).
 * Centralizes the `state_coupling` handling — reopen-confirmation (incl. the set_state 409
 * invoice-guard retry), the auto-started-in-progress toast, the P-switch confirm + switch/undo
 * toast, and error snackbars. Returns handlers plus a `dialogs` node to render in the surface.
 */
export function useClockController({ onMaintenanceChanged } = {}) {
  const { t } = useTranslation();
  const clockIn = useTimeclockStore((s) => s.clockIn);
  const clockOut = useTimeclockStore((s) => s.clockOut);
  const switchWo = useTimeclockStore((s) => s.switchWo);
  const config = useTimeclockStore((s) => s.config);

  const [snack, setSnack] = useState(null);
  const [reopen, setReopen] = useState(null);
  const [switchPrompt, setSwitchPrompt] = useState(null);
  const [busy, setBusy] = useState(false);

  const notify = (severity, message, extra) => setSnack({ severity, message, ...extra });

  const errMsg = (e) => {
    const d = e?.response?.data;
    if (typeof d === 'string') return d;
    if (d?.detail) return d.detail;
    if (d?.message) return d.message;
    if (d && typeof d === 'object') {
      const k = Object.keys(d)[0];
      if (k) return Array.isArray(d[k]) ? d[k][0] : String(d[k]);
    }
    return t('worktime.error.generic');
  };

  const handleCoupling = (resp) => {
    const sc = resp?.state_coupling;
    if (sc?.action === 'needs_reopen_confirmation') {
      setReopen({ maintenanceId: resp.maintenance, state: sc.maintenance_state, busy: false });
    } else if (sc?.action === 'started_in_progress') {
      notify('info', t('worktime.toast.startedInProgress'));
      if (onMaintenanceChanged) onMaintenanceChanged();
    }
  };

  const clockInDay = async () => {
    setBusy(true);
    try {
      await clockIn({ kind: 'day' });
    } catch (e) {
      notify('error', errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const startWo = async (maintenanceId) => {
    setBusy(true);
    try {
      const resp = await clockIn({ kind: 'work_order', maintenanceId });
      handleCoupling(resp);
    } catch (e) {
      notify('error', errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const clockOutSession = async (sessionId) => {
    setBusy(true);
    try {
      await clockOut(sessionId);
    } catch (e) {
      notify('error', errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Meal break (ruokatauko): a kind=break session. Starting one closes any running WO clock.
  const startBreak = async () => {
    setBusy(true);
    try {
      await clockIn({ kind: 'break' });
    } catch (e) {
      notify('error', errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doSwitch = async (toId, fromMaintenanceId) => {
    setSwitchPrompt(null);
    setBusy(true);
    try {
      const resp = await switchWo(toId);
      handleCoupling(resp);
      if (fromMaintenanceId) {
        notify('info', t('worktime.toast.switched'), {
          actionLabel: t('worktime.toast.undo'),
          onAction: () => doSwitch(fromMaintenanceId, null),
        });
      }
    } catch (e) {
      notify('error', errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Switch to another WO: confirm with P-switch unless parallel clocking is enabled.
  const requestSwitch = (toId, { fromReg, toReg, fromMaintenanceId } = {}) => {
    if (config && config.allow_parallel) {
      doSwitch(toId, fromMaintenanceId);
    } else {
      setSwitchPrompt({ toId, fromReg, toReg, fromMaintenanceId });
    }
  };

  const confirmReopen = async (cancelDrafts) => {
    if (!reopen) return;
    const orgId = getOrganizationId();
    setReopen((r) => (r ? { ...r, busy: true } : r));
    try {
      const body = { state: 'in_progress' };
      if (cancelDrafts) body.cancel_draft_invoices = true;
      await axios.patch(`/api/maintenances/${reopen.maintenanceId}/set_state/?organization=${orgId}`, body);
      setReopen(null);
      notify('success', t('worktime.toast.reopened'));
      if (onMaintenanceChanged) onMaintenanceChanged();
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.error === 'draft_invoices_require_cancellation' && !cancelDrafts) {
        await confirmReopen(true); // retry once, cancelling the draft invoices
        return;
      }
      setReopen(null);
      notify('error', d?.message || errMsg(e));
    }
  };

  const dialogs = (
    <>
      <ReopenConfirmDialog
        open={!!reopen}
        maintenanceState={reopen?.state}
        busy={!!reopen?.busy}
        onConfirm={() => confirmReopen(false)}
        onClose={() => setReopen(null)}
      />
      <PSwitchDialog
        open={!!switchPrompt}
        fromReg={switchPrompt?.fromReg}
        toReg={switchPrompt?.toReg}
        busy={busy}
        onConfirm={() => doSwitch(switchPrompt.toId, switchPrompt.fromMaintenanceId)}
        onCancel={() => setSwitchPrompt(null)}
      />
      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack?.severity || 'info'}
          variant="filled"
          onClose={() => setSnack(null)}
          action={
            snack?.onAction ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  snack.onAction();
                  setSnack(null);
                }}
              >
                {snack.actionLabel}
              </Button>
            ) : undefined
          }
          sx={{ width: '100%' }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </>
  );

  return { busy, clockInDay, startWo, clockOutSession, startBreak, requestSwitch, dialogs };
}
